import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { getUserTransactions, addCreditToWallet } from '../../../api/LedgerAPI';
import { updateWalletTransaction, deleteWalletTransaction } from '../../../api/WalletTransactionsAPI';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppPaginator from '../../AppPaginator';
import { Can } from '../../../hooks/usePermissions';
import { handleApiError } from '../../../api/risapi';

// Helper function to format date for datetime-local input
const toLocalISOString = (dateStr) => {
    const date = new Date(dateStr);
    const tzoffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzoffset).toISOString().slice(0, 16);
};

const UserLedgerPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [transactions, setTransactions] = useState({ data: [], last_page: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [triggerFetch, setTriggerFetch] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // State for "Add Fund" Modal
    const [showAddModal, setShowAddModal] = useState(false);
    const [creditData, setCreditData] = useState({ amount: '', description: '', transaction_date: toLocalISOString(new Date()) });
    const [addErrors, setAddErrors] = useState({});

    // State for "Edit Payment" Modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [editErrors, setEditErrors] = useState({});

    const fetchLedger = () => {
        setIsLoading(true);
        getUserTransactions(userId, { page: currentPage }, (response) => {
            if (response.user && response.transactions) {
                setUser(response.user);
                setTransactions(response.transactions);
            } else {
                navigate('/club_portal/members'); // Redirect if user not found
            }
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetchLedger();
    }, [userId, currentPage, triggerFetch, navigate]);


     // Handler for "Add Fund" form changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCreditData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleAddCredit = () => {
        setIsSaving(true);
        addCreditToWallet(userId, creditData, (response) => {
            setIsSaving(false);
            if (response.transaction) {
                setShowAddModal(false);
                Swal.fire('Success!', 'Credit added successfully!', 'success');
                setTriggerFetch(p => !p);
                setCreditData({ amount: '', description: '', transaction_date: toLocalISOString(new Date()) });
            } else {
                handleApiError(response, setAddErrors);
            }
        });
    };

    const handleEditClick = (transaction) => {
        setEditErrors({});
        setEditingTransaction({
            ...transaction,
            transaction_date: toLocalISOString(transaction.transaction_date)
        });
        setShowEditModal(true);
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        setIsSaving(true);
        updateWalletTransaction(editingTransaction.id, editingTransaction, (response) => {
            setIsSaving(false);
            if (response.id) {
                setShowEditModal(false);
                Swal.fire('Updated!', 'The payment has been updated.', 'success');
                setTriggerFetch(p => !p);
            } else {
                handleApiError(response, setEditErrors);
            }
        });
    };

    const handleDeleteClick = (transaction) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `You are about to delete this payment of <strong>₹${transaction.amount}</strong>.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteWalletTransaction(transaction.id, (response) => {
                    if (response.success) {
                        Swal.fire('Deleted!', 'The payment has been deleted.', 'success');
                        setTriggerFetch(p => !p);
                    } else {
                        Swal.fire('Error!', 'Could not delete the payment.', 'error');
                    }
                });
            }
        });
    };

    if (isLoading && !user) return <div className="text-center p-5"><div className="spinner-border"></div></div>;

    return (
        <main>
            <div className="container-fluid">
                <AppBreadCrumb title={`Ledger for ${user?.name}`} />
                 <div className="card mb-3">
                    <div className="card-body bg-primary text-center">
                        <h5 className="card-title">Current Balance</h5>
                        <h2 className={`card-text fw-bold ${user?.current_wallet_balance < 0 ? 'text-danger' : 'text-success'}`}>
                            ₹{Number(user?.current_wallet_balance).toFixed(2)}
                        </h2>
                    </div>
                </div>
                <div className="card">
                    <div className="card-header d-flex justify-content-between">
                        <h5>Transaction History</h5>
                        <Can any={['create-wallet-transaction']}>
                            <button className="btn btn-success btn-sm" onClick={() => setShowAddModal(true)}>
                                <i className="fas fa-plus me-2"></i>Add Funds
                            </button>
                        </Can>
                    </div>
                    <div className="card-body">
                        {isLoading ? <div className="text-center"><div className="spinner-border"></div></div> :
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead><tr><th>Date</th><th>Description</th><th>Debit</th><th>Credit</th><th>Balance</th><th className="text-center">Actions</th></tr></thead>
                                    <tbody>
                                        {transactions.data.length > 0 ? transactions.data.map(tx => (
                                            <tr key={tx.id}>
                                                <td>{new Date(tx.transaction_date).toLocaleString()}</td>
                                                <td>{tx.description}</td>
                                                <td className="text-danger">{tx.type === 'debit' ? `₹${tx.amount}` : '-'}</td>
                                                <td className="text-success">{tx.type === 'credit' ? `₹${tx.amount}` : '-'}</td>
                                                <td><strong>₹{tx.balance_after_transaction}</strong></td>
                                                <td className="text-center">
                                                    {tx.type === 'credit' && tx.transactionable_type !== 'App\\Models\\Contribution' && (
                                                        <div className="btn-group">
                                                            <Can any={['edit-wallet-transaction']}>
                                                                <button className="btn btn-outline-primary btn-sm" onClick={() => handleEditClick(tx)}><i className="fas fa-edit"></i></button>
                                                            </Can>
                                                            <Can any={['delete-wallet-transaction']}>
                                                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteClick(tx)}><i className="fas fa-trash"></i></button>
                                                            </Can>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="6" className="text-center">No transactions found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        }
                        <AppPaginator totalPages={transactions.last_page} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                    </div>
                </div>
            </div>

           {/* --- START: ADD FUND MODAL (CORRECTED) --- */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton><Modal.Title>Add Funds to {user?.name}'s Wallet</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Amount (₹) *</Form.Label>
                        <Form.Control type="number" name="amount" value={creditData.amount} onChange={handleInputChange} isInvalid={!!addErrors.amount} />
                        <Form.Control.Feedback type="invalid">{addErrors.amount}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description *</Form.Label>
                        <Form.Control type="text" name="description" value={creditData.description} onChange={handleInputChange} placeholder="e.g., Cash payment, GPay" isInvalid={!!addErrors.description}/>
                        <Form.Control.Feedback type="invalid">{addErrors.description}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Transaction Date & Time *</Form.Label>
                        <Form.Control type="datetime-local" name="transaction_date" value={creditData.transaction_date} onChange={handleInputChange} isInvalid={!!addErrors.transaction_date} />
                        <Form.Control.Feedback type="invalid">{addErrors.transaction_date}</Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleAddCredit} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Credit'}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* --- END: ADD FUND MODAL --- */}
            
            {/* Edit Payment Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                 <Modal.Header closeButton><Modal.Title>Edit Payment</Modal.Title></Modal.Header>
                 <Form onSubmit={handleUpdate}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Amount (₹) *</Form.Label>
                            <Form.Control type="number" value={editingTransaction?.amount || ''} onChange={(e) => setEditingTransaction(p => ({...p, amount: e.target.value}))} isInvalid={!!editErrors.amount} />
                            <Form.Control.Feedback type="invalid">{editErrors.amount}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description *</Form.Label>
                            <Form.Control type="text" value={editingTransaction?.description || ''} onChange={(e) => setEditingTransaction(p => ({...p, description: e.target.value}))} isInvalid={!!editErrors.description}/>
                            <Form.Control.Feedback type="invalid">{editErrors.description}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Transaction Date & Time *</Form.Label>
                            <Form.Control type="datetime-local" value={editingTransaction?.transaction_date || ''} onChange={(e) => setEditingTransaction(p => ({...p, transaction_date: e.target.value}))} isInvalid={!!editErrors.transaction_date} />
                            <Form.Control.Feedback type="invalid">{editErrors.transaction_date}</Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                 </Form>
            </Modal>
        </main>
    );
};

export default UserLedgerPage;